import React, { useState, useMemo, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Options.css';
import { MenuContext, ForecastContext, WorkspaceContext } from '../../objects/Context';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-icon.svg';
import { ReactComponent as CashIcon } from '../../assets/icons/cash-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/icons/arrow-right-icon.svg';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';

const DAYS_OF_WEEK = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

function Options() {
  const navigate = useNavigate();
  const { setIsMenuCondensed } = useContext(MenuContext);
  const { workspace } = useContext(WorkspaceContext);
  const { currentForecast, setCurrentForecast, setCompiledForecast, setComponents, setForecasts, setActiveScenario, setScenarios, triggerForecastReload } = useContext(ForecastContext);

  const savedWeekStartDay = currentForecast?.calendar?.weekStartDay || 'Monday';
  const savedRevolver = currentForecast?.calendar?.revolver || {};
  const savedRevolverEnabled = Boolean(savedRevolver.enabled);
  const savedMinCashBalance = savedRevolver.minCashBalance != null ? String(savedRevolver.minCashBalance) : '';
  const savedFacilityLimit = savedRevolver.facilityLimit != null ? String(savedRevolver.facilityLimit) : '';
  const savedInterestRate = savedRevolver.interestRate != null ? String(savedRevolver.interestRate) : '';
  const savedCommitmentFeeRate = savedRevolver.commitmentFeeRate != null ? String(savedRevolver.commitmentFeeRate) : '0.5';
  const savedBenchmarkBps = savedRevolver.benchmarkBps != null ? String(savedRevolver.benchmarkBps) : '';

  const [weekStartDay, setWeekStartDay] = useState(savedWeekStartDay);
  const [saving, setSaving] = useState(false);
  const [revolverEnabled, setRevolverEnabled] = useState(savedRevolverEnabled);
  const [minCashBalance, setMinCashBalance] = useState(savedMinCashBalance);
  const [facilityLimit, setFacilityLimit] = useState(savedFacilityLimit);
  const [interestRate, setInterestRate] = useState(savedInterestRate);
  const [commitmentFeeRate, setCommitmentFeeRate] = useState(savedCommitmentFeeRate);
  const [benchmarkBps, setBenchmarkBps] = useState(savedBenchmarkBps);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [revolverSaveAttempted, setRevolverSaveAttempted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const normalizeNumericInput = (value) =>
    String(value ?? '').replace(/[$,%\s,]/g, '');

  const parseNumeric = (value) => {
    const n = normalizeNumericInput(value);
    if (!n) return null;
    const parsed = Number(n);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const hasValidRevolverRequired =
    parseNumeric(minCashBalance) != null &&
    parseNumeric(facilityLimit) != null &&
    parseNumeric(interestRate) != null;

  const hasRevolverChanges =
    revolverEnabled !== savedRevolverEnabled ||
    normalizeNumericInput(minCashBalance) !== normalizeNumericInput(savedMinCashBalance) ||
    normalizeNumericInput(facilityLimit) !== normalizeNumericInput(savedFacilityLimit) ||
    normalizeNumericInput(interestRate) !== normalizeNumericInput(savedInterestRate) ||
    normalizeNumericInput(commitmentFeeRate) !== normalizeNumericInput(savedCommitmentFeeRate) ||
    normalizeNumericInput(benchmarkBps) !== normalizeNumericInput(savedBenchmarkBps);

  const hasChanges = weekStartDay !== savedWeekStartDay || hasRevolverChanges;

  useEffect(() => {
    setIsMenuCondensed(false);
  }, []);

  useEffect(() => {
    if (hasValidRevolverRequired || !revolverEnabled) setRevolverSaveAttempted(false);
  }, [hasValidRevolverRequired, revolverEnabled]);

  useEffect(() => {
    setWeekStartDay(savedWeekStartDay);
    setRevolverEnabled(savedRevolverEnabled);
    setMinCashBalance(savedMinCashBalance);
    setFacilityLimit(savedFacilityLimit);
    setInterestRate(savedInterestRate);
    setCommitmentFeeRate(savedCommitmentFeeRate);
    setBenchmarkBps(savedBenchmarkBps);
  }, [
    savedWeekStartDay,
    savedRevolverEnabled,
    savedMinCashBalance,
    savedFacilityLimit,
    savedInterestRate,
    savedCommitmentFeeRate,
    savedBenchmarkBps
  ]);

  const rollforwardDay = useMemo(() => {
    const idx = DAYS_OF_WEEK.indexOf(weekStartDay);
    return DAYS_OF_WEEK[(idx + 2) % 7];
  }, [weekStartDay]);

  const forecastStartDate = useMemo(() => {
    const today = new Date();
    const currentDayIndex = today.getDay();
    const targetDayIndex = DAYS_OF_WEEK.indexOf(weekStartDay) + 1;
    const adjustedTargetIndex = targetDayIndex === 7 ? 0 : targetDayIndex;

    let daysToGoBack = currentDayIndex - adjustedTargetIndex;
    if (daysToGoBack < 0) daysToGoBack += 7;

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - daysToGoBack);
    return startDate;
  }, [weekStartDay]);

  const forecastEndDate = useMemo(() => {
    const endDate = new Date(forecastStartDate);
    endDate.setDate(forecastStartDate.getDate() + (13 * 7) - 1);
    return endDate;
  }, [forecastStartDate]);

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleSave = async () => {
    if (!hasChanges || saving || !currentForecast?.id) return;
    if (hasRevolverChanges && revolverEnabled && !hasValidRevolverRequired) {
      setRevolverSaveAttempted(true);
      return;
    }
    setSaving(true);
    try {
      let mergedCalendar = currentForecast?.calendar || {};

      if (weekStartDay !== savedWeekStartDay) {
        const optionsResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/update-options`,
          { forecastId: currentForecast.id, weekStartDay },
          { withCredentials: true }
        );
        if (optionsResponse.data?.success && optionsResponse.data?.calendar) {
          mergedCalendar = optionsResponse.data.calendar;
        }
      }

      if (hasRevolverChanges) {
        const revolverResponse = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/update-revolver`,
          {
            forecastId: currentForecast.id,
            revolverEnabled,
            minCashBalance,
            facilityLimit,
            interestRate,
            commitmentFeeRate,
            benchmarkBps,
          },
          { withCredentials: true }
        );

        if (revolverResponse.data?.success && revolverResponse.data?.calendar) {
          mergedCalendar = revolverResponse.data.calendar;
          if (typeof triggerForecastReload === 'function') {
            triggerForecastReload(); /* Refetch compiled forecast so revolver rows appear/ disappear */
          }
        }
      }

      setCurrentForecast(prev => ({
        ...prev,
        calendar: mergedCalendar,
      }));
      if (mergedCalendar?.weekStartDay) {
        setWeekStartDay(mergedCalendar.weekStartDay);
      }
    } catch (err) {
      console.error('Failed to save forecast options:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to save';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteForecast = async () => {
    if (deleting || !currentForecast?.id) return;
    const wsId = currentForecast.workspaceId || workspace?.id;
    if (!wsId) return;

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/forecasts/fully-delete-forecast/${currentForecast.id}/${wsId}`,
        { withCredentials: true }
      );
      if (response.data?.success) {
        setCurrentForecast(response.data.forecast);
        setCompiledForecast(null);
        setComponents(null);
        setActiveScenario(null);
        setScenarios([]);
        setForecasts(prev => prev.map(f => f.id === currentForecast.id ? response.data.forecast : f));
        navigate('/forecast');
      }
    } catch (err) {
      console.error('Failed to delete forecast:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to delete forecast';
      alert(msg);
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="wrapper fs-wrapper">
      {(saving || deleting) && <LoadingScreen message={deleting ? "Deleting forecast..." : "Saving..."} />}
      <div className="fs-header">
        <div className="page-title-group">
          <h1 className="page-title">Forecast Options</h1>
          <p className="page-subtitle">Configure calendar, rollforward, and revolver behavior</p>
        </div>
      </div>
      <div className="fs-content">
        {/* ── Core Forecast Settings ─────────────────────────────── */}
        <div className="fs-section">
          <div className="fs-section-header">
            <CalendarIcon className="fs-section-icon" />
            <span className="fs-section-title">Core Forecast Settings</span>
          </div>

          <div className="fs-section-body">
            {/* Week Start Day + Base Scenario row */}
            <div className="fs-field-row">
              <div className="fs-field">
                <label className="fs-field-label">Week Start Day</label>
                <span className="fs-field-hint">Starting day of each forecast week</span>
                <div className="fs-dropdown-container">
                  <select
                    className="fs-dropdown"
                    value={weekStartDay}
                    onChange={(e) => setWeekStartDay(e.target.value)}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <ChevronDownIcon className="fs-dropdown-icon" />
                </div>
              </div>

              <div className="fs-field fs-field-locked">
                <label className="fs-field-label">Rollforward Day</label>
                <span className="fs-field-hint">2 days after week start day</span>
                <div className="fs-locked-value">{rollforwardDay}</div>
              </div>

              <div className="fs-field fs-field-locked">
                <label className="fs-field-label">Base Scenario</label>
                <span className="fs-field-hint">Scenario used when rolling forward</span>
                <div className="fs-locked-value">Base</div>
              </div>
            </div>

            {/* Calendar Preview */}
            <div className="fs-preview">
              <h3 className="fs-preview-title">Resulting Calendar</h3>
              <div className="fs-preview-grid">
                <div className="fs-preview-item">
                  <span className="fs-preview-label">Frequency</span>
                  <span className="fs-preview-value">Weekly</span>
                </div>
                <div className="fs-preview-item">
                  <span className="fs-preview-label">Duration</span>
                  <span className="fs-preview-value">13 Weeks</span>
                </div>
                <div className="fs-preview-item">
                  <span className="fs-preview-label">Start Date</span>
                  <span className="fs-preview-value">{formatDate(forecastStartDate)}</span>
                </div>
                <div className="fs-preview-item">
                  <span className="fs-preview-label">End Date</span>
                  <span className="fs-preview-value">{formatDate(forecastEndDate)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Revolver / Min Cash Settings ───────────────────────── */}
        <div className="fs-section">
          <div className="fs-section-header">
            <CashIcon className="fs-section-icon" />
            <span className="fs-section-title">Line of Credit (Revolver) + Minimum Cash</span>
          </div>

          <div className="fs-section-body">
            {/* Toggle */}
            <div className="fs-toggle-row">
              <div
                className={`fs-toggle ${revolverEnabled ? 'on' : ''}`}
                onClick={() => setRevolverEnabled(prev => !prev)}
              >
                <div className="fs-toggle-knob" />
              </div>
              <span className="fs-toggle-label">Line of Credit Enabled</span>
              <span className="fs-toggle-status">
                {revolverEnabled ? 'Active — draws and repayments will be calculated' : 'Off'}
              </span>
            </div>

            {/* Revolver fields */}
            <div className={`fs-revolver-fields ${!revolverEnabled ? 'disabled' : ''}`}>
              <div className="fs-field-row">
                <div className="fs-field">
                  <label className="fs-field-label">Minimum Cash Balance <span className="fs-required">*</span></label>
                  <span className="fs-field-hint">Target floor before drawing on revolver</span>
                  <div className="fs-input-prefix">
                    <span className="fs-prefix">$</span>
                    <input
                      type="text"
                      className="fs-input"
                      placeholder="e.g. 100,000"
                      value={minCashBalance}
                      onChange={(e) => setMinCashBalance(e.target.value)}
                    />
                  </div>
                </div>

                <div className="fs-field">
                  <label className="fs-field-label">Facility Limit <span className="fs-required">*</span></label>
                  <span className="fs-field-hint">Maximum available credit on the revolver</span>
                  <div className="fs-input-prefix">
                    <span className="fs-prefix">$</span>
                    <input
                      type="text"
                      className="fs-input"
                      placeholder="e.g. 500,000"
                      value={facilityLimit}
                      onChange={(e) => setFacilityLimit(e.target.value)}
                    />
                  </div>
                </div>

                <div className="fs-field">
                  <label className="fs-field-label">Interest Rate <span className="fs-required">*</span></label>
                  <span className="fs-field-hint">Annual rate applied to outstanding balance</span>
                  <div className="fs-input-suffix">
                    <input
                      type="text"
                      className="fs-input"
                      placeholder="e.g. 6.50"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                    />
                    <span className="fs-suffix">%</span>
                  </div>
                </div>
              </div>

              {revolverSaveAttempted && revolverEnabled && !hasValidRevolverRequired && (
                <div className="fs-revolver-error">
                  Please fill in Minimum Cash Balance, Facility Limit, and Interest Rate to save revolver settings.
                </div>
              )}

              {/* Advanced revolver settings */}
              <div className="fs-advanced">
                <button
                  type="button"
                  className="fs-advanced-toggle"
                  onClick={() => setAdvancedOpen(prev => !prev)}
                  aria-expanded={advancedOpen}
                >
                  <ChevronDownIcon className={`fs-advanced-chevron ${advancedOpen ? 'open' : ''}`} />
                  <span>Advanced</span>
                </button>
                {advancedOpen && (
                  <div className="fs-advanced-body">
                    <div className="fs-field-row">
                      <div className="fs-field">
                        <label className="fs-field-label">Commitment Fee %</label>
                        <span className="fs-field-hint">Annual fee on unused facility capacity (e.g. 0.25–0.5)</span>
                        <div className="fs-input-suffix">
                          <input
                            type="text"
                            className="fs-input"
                            placeholder="e.g. 0.5"
                            value={commitmentFeeRate}
                            onChange={(e) => setCommitmentFeeRate(e.target.value)}
                          />
                          <span className="fs-suffix">%</span>
                        </div>
                      </div>
                      <div className="fs-field">
                        <label className="fs-field-label">LIBOR / SOFR (bps)</label>
                        <span className="fs-field-hint">Benchmark spread in basis points for variable rate</span>
                        <div className="fs-input-suffix">
                          <input
                            type="text"
                            className="fs-input"
                            placeholder="e.g. 250"
                            value={benchmarkBps}
                            onChange={(e) => setBenchmarkBps(e.target.value)}
                          />
                          <span className="fs-suffix">bps</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button
          className="fs-delete-btn"
          onClick={() => setShowDeleteConfirm(true)}
        >
          Delete Forecast
        </button>
      </div>
      <div className="fs-bottom-bar">
        <button
          className={`fs-save-btn ${hasChanges ? 'active' : ''}`}
          disabled={!hasChanges || saving}
          onClick={handleSave}
        >
          Save
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="fs-delete-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="fs-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="fs-delete-modal-title">Delete Forecast</h3>
            <p className="fs-delete-modal-body">
              This will permanently remove all forecast data — including threads, messages, logs, snapshots, and scenarios — and reset the forecast to a blank state. This action cannot be undone.
            </p>
            <div className="fs-delete-modal-actions">
              <button
                className="fs-delete-modal-cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="fs-delete-modal-confirm"
                onClick={handleDeleteForecast}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Forecast'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Options;
